import fortressLogo from "../assets/fortress-logo.jpg";
import { contact as sharedContact } from "../data/contact";

const footerGroups = [
  {
    title: "Company",
    links: [
      ["About us", "/about"],
      ["Support", "/support"],
      ["Careers", "/careers"],
      ["Insights", "/insights"],
      ["Case studies", "/case-studies"],
    ],
  },
  {
    title: "Products",
    links: [
      ["Custody", "/solutions"],
      ["Private access", "/access"],
      ["Safe boxes", "/boxes"],
      ["Pricing", "/pricing"],
    ],
  },
  {
    title: "Clients",
    links: [
      ["Individuals", "/clients"],
      ["Corporate", "/clients"],
      ["Travellers", "/clients"],
      ["Testimonials", "/clients"],
      ["Get started", "/get-started"],
    ],
  },
  {
    title: "Governance",
    links: [
      ["Security", "/security"],
      ["Insurance", "/insurance"],
      ["Tracking", "/tracking"],
      ["FAQs", "/faqs"],
    ],
  },
  {
    title: "Policies",
    links: [
      ["Payment policy", "/payment-policy"],
      ["Privacy policy", "/privacy-policy"],
      ["Book a visit", "/contact"],
      ["Client portal", "/login"],
    ],
  },
];

function Footer({
  contact = sharedContact,
  footerGroups: groups = footerGroups,
  logo = fortressLogo,
}) {
  const currentYear = new Date().getFullYear();
  const mapQuery = encodeURIComponent(contact.location || "Nairobi, Kenya");
  const mapEmbedUrl = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  const mapOpenUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  return (
    <footer className="relative overflow-hidden bg-black text-white">
      {/* Top CTA bar */}
      <div className="relative z-10 bg-brand-gold px-5 py-6 text-ink sm:px-8 lg:px-10">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-4xl text-balance text-xl font-light leading-tight tracking-[-0.035em] sm:text-2xl md:text-3xl">
            Automated safe deposit infrastructure, built for private and institutional clients.
          </p>

          <a
            href="/solutions"
            className="inline-flex h-12 w-full shrink-0 items-center justify-center bg-black px-7 text-sm font-medium text-white transition duration-300 hover:bg-ink sm:w-auto"
          >
            Explore solutions
          </a>
        </div>
      </div>

      {/* Main footer */}
      <div className="relative bg-brand-blue px-5 pb-8 pt-16 text-pale/80 sm:px-8 lg:px-10 lg:pt-20">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(200,168,81,0.12),transparent_30%),linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[length:auto,140px_140px,140px_140px]"
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-[1440px]">
          <nav
            className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6"
            aria-label="Footer navigation"
          >
            {groups.map((group) => (
              <div key={group.title}>
                <h2 className="mb-5 flex items-center gap-2 text-sm font-medium text-brand-gold">
                  <span className="text-brand-gold">•</span>
                  {group.title}
                </h2>

                <ul className="grid gap-3">
                  {group.links.map(([label, href]) => (
                    <li key={label}>
                      <a
                        className="text-base text-pale/80 transition duration-300 hover:text-brand-gold"
                        href={href}
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div>
              <h2 className="mb-5 flex items-center gap-2 text-sm font-medium text-brand-gold">
                <span className="text-brand-gold">•</span>
                Contact
              </h2>

              <ul className="grid gap-3">
                <li>
                  <a
                    className="text-base text-pale/80 transition duration-300 hover:text-brand-gold"
                    href={`mailto:${contact.email}`}
                  >
                    {contact.email}
                  </a>
                </li>

                <li>
                  <a
                    className="text-base text-pale/80 transition duration-300 hover:text-brand-gold"
                    href={mapOpenUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {contact.location}
                  </a>
                </li>

                {contact.poBox ? (
                  <li className="text-base leading-relaxed text-pale/80">
                    {contact.poBox}
                  </li>
                ) : null}

                {(contact.phones || []).map((phone) => (
                  <li key={phone}>
                    <a
                      className="text-base text-pale/80 transition duration-300 hover:text-brand-gold"
                      href={`tel:${phone.replace(/\s/g, "")}`}
                    >
                      {phone}
                    </a>
                  </li>
                ))}

                {contact.hours ? (
                  <li className="text-base text-pale/80">{contact.hours}</li>
                ) : null}

                <li>
                  <a
                    className="text-base text-pale/80 transition duration-300 hover:text-brand-gold"
                    href="/contact"
                  >
                    Book a visit
                  </a>
                </li>

                <li>
                  <a
                    className="text-base text-pale/80 transition duration-300 hover:text-brand-gold"
                    href={contact.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                  >
                    WhatsApp
                  </a>
                </li>
              </ul>
            </div>
          </nav>

          {/* Embedded interactive map */}
          <div className="mt-14 grid gap-6 border-t border-white/10 pt-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-stretch">
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-sm font-medium text-brand-gold">
                <span className="text-brand-gold">•</span>
                Find us
              </h2>

              <p className="max-w-md text-base leading-relaxed text-pale/75">
                Use the interactive map to view our listed location, zoom, pan, or open
                directions in Google Maps.
              </p>

              <a
                href={mapOpenUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex h-12 items-center justify-center bg-brand-gold px-7 text-sm font-medium text-ink transition duration-300 hover:bg-white"
              >
                Open in Google Maps
              </a>
            </div>

            <div className="relative min-h-[18rem] overflow-hidden border border-white/10 bg-black">
              <iframe
                title={`Map showing ${contact.location}`}
                src={mapEmbedUrl}
                className="h-full min-h-[18rem] w-full"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />

              <a
                href={mapOpenUrl}
                target="_blank"
                rel="noreferrer"
                className="absolute bottom-4 right-4 z-10 bg-black px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white transition duration-300 hover:bg-brand-gold hover:text-ink"
              >
                Directions ↗
              </a>
            </div>
          </div>

          <div className="mt-12 border-t border-white/10 pt-7">
            <div className="flex flex-col gap-3 text-sm text-pale/60 sm:items-end">
              <span>{currentYear} Fortress Safe LTD. All rights reserved.</span>
              <span>Private custody, Nairobi.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
